import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Download } from 'lucide-react';
import { SortableTh, SortDir } from '@/components/SortableTh';

interface Bucket {
  region: string;
  provincia: string | null;
  distrito: string | null;
  n_listas: number;
  n_listas_ok: number;
  n_listas_observacion: number;
  n_listas_bloqueante: number;
  n_listas_inscritas_jee: number;
  n_candidatos: number;
  n_designados_pendientes: number;
  n_val_ok: number;
  n_val_observacion: number;
  n_val_bloqueante: number;
  n_djhv_completa: number;
  n_djhv_confirmada: number;
  n_docs_aplica: number;
  n_docs_aprobados: number;
  pct_docs: number;
}

interface AvanceData {
  nivel_agrupacion: string;
  totales: Bucket & Record<string, any>;
  buckets: Bucket[];
}

interface Filtros {
  regiones: string[];
  provincias_por_region: Record<string, string[]>;
  distritos_por_prov: Record<string, string[]>;
  tipos_eleccion: string[];
  niveles: string[];
}

function pctBar({ value, color }: { value: number; color: string }) {
  return (
    <div className="w-full bg-gray-200 rounded h-1.5">
      <div className={`${color} h-1.5 rounded`} style={{ width: `${Math.min(100, value)}%` }} />
    </div>
  );
}

export function Avance() {
  const [nivelAgrupacion, setNivelAgrupacion] = useState<'region' | 'provincia' | 'distrito'>('region');
  const [region, setRegion] = useState('');
  const [provincia, setProvincia] = useState('');
  const [distrito, setDistrito] = useState('');
  const [nivel, setNivel] = useState('');
  const [tipoEleccion, setTipoEleccion] = useState('');
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  const onSort = (key: string, dir: SortDir) => {
    setSortBy(dir ? key : null);
    setSortDir(dir);
  };

  const { data: filtros } = useQuery<Filtros>({
    queryKey: ['avance-filtros'],
    queryFn: async () => (await api.get<Filtros>('/avance/filtros')).data,
  });

  const params = {
    nivel_agrupacion: nivelAgrupacion,
    region: region || undefined,
    provincia: provincia || undefined,
    distrito: distrito || undefined,
    nivel: nivel || undefined,
    tipo_eleccion: tipoEleccion || undefined,
  };
  const { data, isLoading } = useQuery<AvanceData>({
    queryKey: ['avance', params],
    queryFn: async () => (await api.get<AvanceData>('/avance/jerarquico', { params })).data,
  });

  const onExport = async () => {
    const r = await api.get('/avance/export.xlsx', { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `avance_${nivelAgrupacion}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const provincias = region ? (filtros?.provincias_por_region?.[region] ?? []) : [];
  const distritos = region && provincia ? (filtros?.distritos_por_prov?.[`${region}|${provincia}`] ?? []) : [];

  const sortedBuckets = useMemo(() => {
    const arr = [...(data?.buckets ?? [])];
    if (!sortBy || !sortDir) return arr;
    arr.sort((a: any, b: any) => {
      const va = a[sortBy], vb = b[sortBy];
      if (va === vb) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [data?.buckets, sortBy, sortDir]);

  return (
    <div className="p-6 space-y-4 max-w-[1500px]">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-arpp-primary">Tablero de Avance</h1>
          <p className="text-xs text-gray-600">
            Métricas agregadas por circunscripción · DJHV · Documentos · Validación · JEE
          </p>
        </div>
        <button
          onClick={onExport}
          className="bg-green-700 text-white px-3 py-2 rounded text-xs hover:bg-green-800 flex items-center gap-2"
        >
          <Download size={14} /> Exportar Excel
        </button>
      </header>

      <div className="bg-white border rounded p-3 flex flex-wrap gap-3 items-end text-xs">
        <div>
          <label className="block text-[11px] text-gray-600">Agrupar por</label>
          <select
            value={nivelAgrupacion}
            onChange={(e) => setNivelAgrupacion(e.target.value as any)}
            className="border rounded px-2 py-1"
          >
            <option value="region">Región</option>
            <option value="provincia">Provincia</option>
            <option value="distrito">Distrito</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Región</label>
          <select value={region} onChange={(e) => { setRegion(e.target.value); setProvincia(''); setDistrito(''); }} className="border rounded px-2 py-1 w-44">
            <option value="">Todas</option>
            {filtros?.regiones.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Provincia</label>
          <select value={provincia} onChange={(e) => { setProvincia(e.target.value); setDistrito(''); }} disabled={!region} className="border rounded px-2 py-1 w-44 disabled:bg-gray-50">
            <option value="">{region ? 'Todas' : 'Selecciona región'}</option>
            {provincias.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Distrito</label>
          <select value={distrito} onChange={(e) => setDistrito(e.target.value)} disabled={!provincia} className="border rounded px-2 py-1 w-44 disabled:bg-gray-50">
            <option value="">{provincia ? 'Todos' : 'Selecciona prov'}</option>
            {distritos.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Nivel elección</label>
          <select value={nivel} onChange={(e) => setNivel(e.target.value)} className="border rounded px-2 py-1">
            <option value="">Todos</option>
            {filtros?.niveles.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-[11px] text-gray-600">Tipo elección</label>
          <select value={tipoEleccion} onChange={(e) => setTipoEleccion(e.target.value)} className="border rounded px-2 py-1 w-full">
            <option value="">Todos</option>
            {filtros?.tipos_eleccion.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {/* Totales */}
      {data && (
        <section className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div className="border rounded p-3 bg-blue-50">
            <div className="text-2xl font-bold">{data.totales.n_listas.toLocaleString('es-PE')}</div>
            <div className="text-[10px] text-gray-700 uppercase">Listas</div>
            <div className="text-[10px] text-gray-600 mt-0.5">
              {data.totales.n_listas_ok} ok · {data.totales.n_listas_observacion} obs · <span className="text-red-700">{data.totales.n_listas_bloqueante} bloq</span>
            </div>
          </div>
          <div className="border rounded p-3 bg-blue-50">
            <div className="text-2xl font-bold">{data.totales.n_candidatos.toLocaleString('es-PE')}</div>
            <div className="text-[10px] text-gray-700 uppercase">Candidatos</div>
            <div className="text-[10px] text-gray-600 mt-0.5">
              {data.totales.n_val_ok} ok · {data.totales.n_val_observacion} obs · <span className="text-red-700">{data.totales.n_val_bloqueante} bloq</span>
            </div>
          </div>
          <div className="border rounded p-3 bg-amber-50">
            <div className="text-2xl font-bold">{data.totales.n_designados_pendientes.toLocaleString('es-PE')}</div>
            <div className="text-[10px] text-gray-700 uppercase">Designados pdte</div>
          </div>
          <div className="border rounded p-3 bg-purple-50">
            <div className="text-2xl font-bold">
              {data.totales.n_djhv_completa.toLocaleString('es-PE')}
            </div>
            <div className="text-[10px] text-gray-700 uppercase">DJHV completas</div>
            <div className="text-[10px] text-gray-600 mt-0.5">{data.totales.n_djhv_confirmada} confirmadas</div>
          </div>
          <div className="border rounded p-3 bg-green-50">
            <div className="text-2xl font-bold">{data.totales.pct_docs}%</div>
            <div className="text-[10px] text-gray-700 uppercase">Docs aprobados</div>
            <div className="text-[10px] text-gray-600 mt-0.5">{data.totales.n_docs_aprobados}/{data.totales.n_docs_aplica}</div>
          </div>
        </section>
      )}

      {/* Tabla por bucket */}
      <div className="bg-white border rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <SortableTh label="Región" sortKey="region" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              {nivelAgrupacion !== 'region' && (
                <SortableTh label="Provincia" sortKey="provincia" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              )}
              {nivelAgrupacion === 'distrito' && (
                <SortableTh label="Distrito" sortKey="distrito" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              )}
              <SortableTh label="Listas" align="right" sortKey="n_listas" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="🟢" align="right" sortKey="n_listas_ok" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="⚠" align="right" sortKey="n_listas_observacion" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="🔴" align="right" sortKey="n_listas_bloqueante" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="JEE" align="right" sortKey="n_listas_inscritas_jee" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Cand" align="right" sortKey="n_candidatos" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Desig pdte" align="right" sortKey="n_designados_pendientes" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="DJHV ✓" align="right" sortKey="n_djhv_completa" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="DJHV +" align="right" sortKey="n_djhv_confirmada" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Docs" align="right" sortKey="n_docs_aprobados" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="% Docs" align="right" sortKey="pct_docs" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={14} className="p-6 text-center text-gray-500">Cargando…</td></tr>
            )}
            {sortedBuckets.map((b, i) => (
              <tr key={i} className="border-t hover:bg-gray-50">
                <td className="px-2 py-1.5 font-medium">{b.region}</td>
                {nivelAgrupacion !== 'region' && <td className="px-2 py-1.5">{b.provincia ?? '—'}</td>}
                {nivelAgrupacion === 'distrito' && <td className="px-2 py-1.5">{b.distrito ?? '—'}</td>}
                <td className="px-2 py-1.5 text-right">{b.n_listas}</td>
                <td className="px-2 py-1.5 text-right text-green-700">{b.n_listas_ok}</td>
                <td className="px-2 py-1.5 text-right text-amber-700">{b.n_listas_observacion}</td>
                <td className="px-2 py-1.5 text-right text-red-700">{b.n_listas_bloqueante}</td>
                <td className="px-2 py-1.5 text-right">{b.n_listas_inscritas_jee}</td>
                <td className="px-2 py-1.5 text-right">{b.n_candidatos}</td>
                <td className="px-2 py-1.5 text-right text-amber-700">{b.n_designados_pendientes}</td>
                <td className="px-2 py-1.5 text-right">{b.n_djhv_completa}</td>
                <td className="px-2 py-1.5 text-right text-green-700">{b.n_djhv_confirmada}</td>
                <td className="px-2 py-1.5 text-right">{b.n_docs_aprobados}/{b.n_docs_aplica}</td>
                <td className="px-2 py-1.5 text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <span className="w-10 text-right">{b.pct_docs}%</span>
                    <div className="w-12">{pctBar({ value: b.pct_docs, color: b.pct_docs >= 80 ? 'bg-green-600' : b.pct_docs >= 40 ? 'bg-amber-500' : 'bg-red-500' })}</div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
